import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  ModalProvider,
  Button,
  Image,
  Label,
  Input,
  Select,
  Stripe,
  MobileScrollButton,
  TosCheckbox,
} from "components";
import { toast } from "react-toastify";
import { stripeKey } from "utils";
import { useLockBodyScroll } from "hooks";
import { PRODUCTS } from "api";
import * as S from "./StripeModal.styled";
import { useLocation, useHistory } from "react-router-dom";
import { countries } from "consts";

export default ({
  paywallSettings,
  eventId,
  close,
  registerar,
  next,
  donationAmount,
  title,
  preventClickOut,
  eventReminderOptOut,
}) => {
  const [order, setOrder] = useState({
    email: registerar?.email,
    country: "United States of America (the)",
  });
  const [selectedCountry, setSelectedCountry] = useState({
    name: "United States of America (the)",
  });
  const [stripe, setStripe] = useState();
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState();
  const [cardExpiry, setCardExpiry] = useState();
  const [cardCVC, setCardCVC] = useState();
  const [tosAccepted, setTosAccepted] = useState(false);
  const location = useLocation();
  const history = useHistory();
  useLockBodyScroll();

  useEffect(() => {
    const _stripe = window.Stripe(stripeKey);
    setStripe(_stripe);
  }, []);

  const toggleTos = () => setTosAccepted(!tosAccepted);

  const updateOrder = (e) => {
    order[e.target.name] = e.target.value;
    setOrder(order);
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    order.country = country.name;
    setOrder(order);
  };

  const isUSA = selectedCountry?.name === "United States of America (the)";

  // Create country options with US first, Canada second, then alphabetically
  const usOption = { name: "United States of America (the)" };
  const canadaOption = { name: "Canada" };
  const otherCountries = countries
    .filter(
      (c) =>
        c.country !== "United States of America (the)" && c.country !== "Canada"
    )
    .map((c) => ({ name: c.country }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const countryOptions = [usOption, canadaOption, ...otherCountries];

  const purchase = async () => {
    setLoading(true);
    if (!order.email) {
      toast.error("Please enter an email for this order", { autoClose: 5000 });
    } else if (!order.name) {
      toast.error("Please enter a name for this order", { autoClose: 5000 });
    } else if (!order.street) {
      toast.error("Please enter a street for this order", { autoClose: 5000 });
    } else if (!order.country) {
      toast.error("Please select a country for this order", {
        autoClose: 5000,
      });
    } else if (!order.city) {
      toast.error("Please enter a city for this order", { autoClose: 5000 });
    } else if (isUSA && !order.state) {
      toast.error("Please enter a state for this order", { autoClose: 5000 });
    } else if (isUSA && !order.zip) {
      toast.error("Please enter a zip for this order", { autoClose: 5000 });
    } else if (!cardNumber || cardNumber._empty || cardNumber._invalid) {
      toast.error("Please enter a valid card number", { autoClose: 5000 });
    } else if (!cardExpiry || cardExpiry._empty || cardExpiry._invalid) {
      toast.error("Please enter your card's expiration date", {
        autoClose: 5000,
      });
    } else if (!cardCVC || cardCVC._empty || cardCVC._invalid) {
      toast.error("Please enter your card's CVC", { autoClose: 5000 });
    } else {
      try {
        if (isUSA) {
          order.address =
            order.street +
            ", " +
            order.city +
            ", " +
            order.state +
            ", " +
            order.zip;
        } else {
          order.address =
            order.street + ", " + order.city + ", " + order.country;
          order.state = order.state || "";
          order.zip = order.zip || "";
        }
        order.prepay = true;
        delete order.street;
        delete order.city;
        delete order.state;
        delete order.zip;

        await donateToFilmMaker(order);

        setLoading(false);
      } catch (err) {
        console.log("Error creating order [3]", err);
        toast.error(
          err.response?.data?.message ||
            "An unexpeced error occured while creating your order",
          {
            autoClose: 5000,
          }
        );
      }
    }
    setLoading(false);
  };

  const donateToFilmMaker = async (order) => {
    if (donationAmount && donationAmount > 0) {
      let _donationProductId = paywallSettings.donationProductId
        ? paywallSettings.donationProductId
        : null;

      const _updatedSettings = await PRODUCTS.newDonationProductId2(
        paywallSettings,
        eventId,
        donationAmount
      );
      _donationProductId = _updatedSettings.donationProductId;

      const _donation = { ...order };
      const _donationToken = await stripe.createToken(cardNumber);
      location.afterWatch && (_donation.afterWatching = true);
      history.push({ afterWatch: false });

      _donation.products = [
        {
          id: _donationProductId,
          quantity: 1,
        },
      ];

      _donation.eventReminderOptOut = eventReminderOptOut || false;

      _donation.token = _donationToken.token.id;
      const res = await PRODUCTS.purchase(eventId, _donation);

      if (res.status === 201) {
        toast.success(
          "Donation successful, you will receive an email with your receipt",
          { autoClose: 6000 }
        );
        close();
        next();
      } else {
        console.log("Error creating donation [2] StatusText", res);
        toast.error(
          res.data?.message ||
            "An unexpeced error occured while creating your order",
          {
            autoClose: 5000,
          }
        );
      }
    }
  };

  return (
    <Flex>
      <ModalProvider
        width="1100px"
        margin="64px"
        light
        show={true}
        close={() => close()}
        preventClickOut={preventClickOut}
      >
        <S.StripeBox>
          <S.Summary direction="column" justify="space-between">
            <Flex direction="column" align="center">
              <Flex direction="column" align="flex-start">
                <S.Back pointer onClick={() => close()}>
                  <img
                    alt="back-button"
                    src={require("assets/images/icon-chevron-left.svg").default}
                  />
                  <Text size="15px">Back</Text>
                </S.Back>
                <Text color="rgba(255, 255, 255)" size="19px" margin="0 0 56px">
                  Order Summary
                </Text>
                {paywallSettings?.enablePaywall && (
                  <>
                    <Text
                      color="rgba(255, 255, 255, 0.6)"
                      size="16px"
                      margin="0 0 6px"
                    >
                      Ticket to {title}
                    </Text>
                    <Text
                      color="rgba(255, 255, 255, 0.9)"
                      size="36px"
                      margin="0 0 18px"
                    >
                      ${(paywallSettings?.price * 1).toFixed(2)}
                    </Text>
                  </>
                )}
              </Flex>
              {paywallSettings?.enablePaywall && (
                <Flex justify="center">
                  <S.Line />
                </Flex>
              )}
              {donationAmount > 0 && (
                <>
                  <Flex
                    direction="column"
                    align="flex-start"
                    padding="30px 0 0 0"
                  >
                    <Text
                      color="rgba(255, 255, 255, 0.6)"
                      size="16px"
                      margin="0 0 6px"
                    >
                      One time donation
                    </Text>
                    <Text
                      color="rgba(255, 255, 255, 0.9)"
                      size="36px"
                      margin="0 0 18px"
                    >
                      ${(donationAmount * 1).toFixed(2)}
                    </Text>
                  </Flex>
                  <Flex justify="center">
                    <S.Line />
                  </Flex>
                </>
              )}
            </Flex>

            <MobileScrollButton location="card-info" />

            <Flex direction="column" align="center">
              <Flex justify="center">
                <a
                  href="https://stripe.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    width="100px"
                    src={require("assets/images/powered-by-stripe.png").default}
                  />
                </a>
                <Text
                  color="rgba(255, 255, 255, 0.3)"
                  size="12px"
                  margin="0 0 0 16px"
                >
                  |
                </Text>
                <a href="/" target="_blank" rel="noopener noreferrer">
                  <Text
                    color="rgba(255, 255, 255, 0.3)"
                    size="12px"
                    margin="0 0 0 16px"
                  >
                    Terms
                  </Text>
                </a>
                <a href="/" target="_blank" rel="noopener noreferrer">
                  <Text
                    color="rgba(255, 255, 255, 0.3)"
                    size="12px"
                    margin="0 0 0 16px"
                  >
                    Privacy
                  </Text>
                </a>
              </Flex>
            </Flex>
          </S.Summary>
          <Flex
            direction="column"
            align="flex-start"
            padding="60px"
            id="card-info"
          >
            <Text size="19px" margin="0 0 24px">
              Pay with card
            </Text>

            <Label>Card Information</Label>
            <Stripe
              stripe={stripe}
              setCardNumber={(e) => setCardNumber(e)}
              setCardExpiry={(e) => setCardExpiry(e)}
              setCardCVC={(e) => setCardCVC(e)}
            />

            <Label>Name on card</Label>
            <Input
              value={order.name}
              name="name"
              onChange={updateOrder}
              margin="0 0 24px"
              placeholder="name"
            />

            <Label>Country</Label>
            <Select
              options={countryOptions}
              selected={selectedCountry}
              setSelected={handleCountryChange}
              placeholder="Select a country"
              margin="0 0 24px"
              width="100%"
              toLowerCase={false}
            />

            <Label>Street</Label>
            <Input
              radius="6px"
              value={order.street}
              name="street"
              onChange={updateOrder}
              margin="0 0 24px"
              placeholder="street"
            />

            <Label>City</Label>
            <Input
              radius="6px"
              value={order.city}
              name="city"
              onChange={updateOrder}
              margin="0 0 24px -1px"
              placeholder="city"
            />

            {isUSA && (
              <Flex align="start">
                <Flex direction="column" align="flex-start" relative>
                  <Label>State</Label>
                  <Input
                    radius="6px 0px 0px 6px"
                    value={order.state}
                    name="state"
                    onChange={updateOrder}
                    margin="0 0 24px"
                    placeholder="state"
                  />
                </Flex>
                <Flex direction="column" align="flex-start" relative>
                  <Label>Zip</Label>
                  <Input
                    radius="0px 6px 6px 0px"
                    value={order.zip}
                    name="zip"
                    onChange={updateOrder}
                    margin="0 0 24px -1px"
                    placeholder="zip"
                  />
                </Flex>
              </Flex>
            )}
            {!isUSA && selectedCountry && (
              <Flex align="start">
                <Flex
                  direction="column"
                  align="flex-start"
                  relative
                  width="50%"
                >
                  <Label>State (optional)</Label>
                  <Input
                    radius="6px 0px 0px 6px"
                    value={order.state}
                    name="state"
                    onChange={updateOrder}
                    margin="0 0 24px"
                    placeholder="state"
                  />
                </Flex>
                <Flex
                  direction="column"
                  align="flex-start"
                  relative
                  width="50%"
                >
                  <Label>Postal Code (optional)</Label>
                  <Input
                    radius="0px 6px 6px 0px"
                    value={order.zip}
                    name="zip"
                    onChange={updateOrder}
                    margin="0 0 24px -1px"
                    placeholder="postal code"
                  />
                </Flex>
              </Flex>
            )}
            {!registerar.email && (
              <>
                <Label>Email receipt to:</Label>
                <Input
                  value={order.email}
                  name="email"
                  onChange={updateOrder}
                  margin="0 0 24px"
                  placeholder="email"
                />
              </>
            )}

            <TosCheckbox
              tosAccepted={tosAccepted}
              toggleTos={toggleTos}
              color="white"
            />

            {loading ? (
              <Button margin="16px 0 64px" className="medium" width="100%">
                Submitting order...
              </Button>
            ) : (
              <Button
                margin="16px 0 64px"
                icon={require("assets/images/icon-lock.svg").default}
                className={`medium ${!tosAccepted ? "disabled" : ""}`}
                width="100%"
                onClick={() => tosAccepted && purchase()}
              >
                Pay $
                {(
                  (paywallSettings?.enablePaywall
                    ? paywallSettings?.price * 1
                    : 0) +
                  donationAmount * 1
                ).toFixed(2)}
              </Button>
            )}
          </Flex>
        </S.StripeBox>
      </ModalProvider>
    </Flex>
  );
};
